import { forwardRef } from 'react';
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, ReferenceLine } from 'recharts';

/**
 * Off-screen card used to generate a shareable PNG of the user's
 * weight progress. Rendered with a fixed pixel width so the captured
 * image looks identical across devices.
 *
 * Visual reference: IMG_6211 (red top stripe + Slimmy logo + name +
 * 4 stat cards + Andamento Peso chart).
 */
const WeightShareCard = forwardRef(function WeightShareCard(
  {
    name,
    chartData,
    chartTitle,
    targetWeight,
    weightLost,
    daysCount,
    streakDays,
    weightUnit = 'kg',
    formatNumber = (n) => n,
    convertWeight = (n) => n,
    labels = {
      lost: 'Peso perso',
      days: 'Giorni',
      streak: 'Serie',
      goal: 'Obiettivo',
      actual: 'Peso reale',
      expected: 'Curva prevista',
    },
  },
  ref
) {
  // Compute Y-axis domain with a bit of padding so the curve never
  // touches the edges of the chart.
  const allValues = chartData
    .flatMap((d) => [d.actual, d.expected])
    .filter((v) => v != null && !isNaN(v));
  const minVal = allValues.length ? Math.min(...allValues, targetWeight) : targetWeight;
  const maxVal = allValues.length ? Math.max(...allValues, targetWeight) : targetWeight + 10;
  const padding = Math.max(1, (maxVal - minVal) * 0.1);
  const yDomain = [Math.floor(minVal - padding), Math.ceil(maxVal + padding)];

  return (
    <div
      ref={ref}
      style={{
        width: 720,
        background: '#FFFFFF',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        color: '#0F172A',
        boxSizing: 'border-box',
        padding: '0 0 28px 0',
        position: 'relative',
      }}
      data-testid="weight-share-card"
    >
      {/* Top red stripe */}
      <div style={{ height: 4, background: '#E8334A', width: '100%' }} />

      {/* Header — Slimmy + name */}
      <div style={{ padding: '28px 32px 18px 32px' }}>
        <div style={{ color: '#E8334A', fontWeight: 700, fontSize: 22, letterSpacing: 0.3 }}>
          Slimmy
        </div>
        <div style={{ color: '#0F172A', fontWeight: 800, fontSize: 44, lineHeight: 1.1, marginTop: 4 }}>
          {name}
        </div>
      </div>

      {/* Stats row — 4 cards */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 14,
          padding: '0 32px',
        }}
      >
        <Stat label={labels.lost} value={`-${formatNumber(convertWeight(weightLost), 1)}`} color="#16A34A" />
        <Stat label={labels.days} value={String(daysCount)} color="#1d3557" />
        <Stat label={labels.streak} value={String(streakDays)} color="#F59E0B" />
        <Stat label={labels.goal} value={String(formatNumber(convertWeight(targetWeight), 0))} color="#E8334A" />
      </div>

      {/* Chart card */}
      <div
        style={{
          margin: '24px 32px 0 32px',
          border: '1px solid #E5E7EB',
          borderRadius: 14,
          padding: '20px 18px 14px 18px',
          background: '#FFFFFF',
        }}
      >
        <div style={{ fontSize: 22, fontWeight: 700, color: '#0F172A', marginBottom: 12 }}>
          {chartTitle}
        </div>
        <div style={{ width: '100%', height: 320 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={chartData}
              margin={{ top: 5, right: 12, bottom: 0, left: -4 }}
            >
              <XAxis
                dataKey="label"
                stroke="#94A3B8"
                fontSize={14}
                tickLine={false}
                axisLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                domain={yDomain}
                stroke="#94A3B8"
                fontSize={14}
                tickLine={false}
                axisLine={false}
                width={48}
              />
              <ReferenceLine
                y={targetWeight}
                stroke="#16A34A"
                strokeDasharray="4 4"
                strokeOpacity={0.6}
              />
              <Line
                type="monotone"
                dataKey="expected"
                stroke="#1B3A6B"
                strokeWidth={2.5}
                strokeDasharray="8 4"
                dot={false}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="actual"
                stroke="#16A34A"
                strokeWidth={3.5}
                dot={{ fill: '#16A34A', strokeWidth: 0, r: 5 }}
                connectNulls
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Legend */}
        <div style={{ display: 'flex', gap: 24, justifyContent: 'center', marginTop: 12, fontSize: 14, color: '#475569' }}>
          <LegendItem color="#16A34A" label={labels.actual} />
          <LegendItem color="#1B3A6B" dashed label={labels.expected} />
        </div>
      </div>

      {/* Footer brand */}
      <div style={{ marginTop: 20, textAlign: 'center', fontSize: 13, color: '#94A3B8' }}>
        slimmy.app
      </div>
    </div>
  );
});

function Stat({ label, value, color }) {
  return (
    <div
      style={{
        border: '1px solid #E5E7EB',
        borderRadius: 12,
        padding: '14px 8px',
        textAlign: 'center',
        background: '#FFFFFF',
      }}
    >
      <div style={{ fontSize: 36, fontWeight: 800, color, lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 15, color: '#64748B', marginTop: 6, fontWeight: 500 }}>{label}</div>
    </div>
  );
}

function LegendItem({ color, dashed, label }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div
        style={{
          width: 22,
          height: 0,
          borderTop: dashed ? `2.5px dashed ${color}` : `2.5px solid ${color}`,
        }}
      />
      <span>{label}</span>
    </div>
  );
}

export default WeightShareCard;
